import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { publicAPI, paymentAPI, landingSettingsAPI, purchaseAPI, uploadAPI } from '@/lib/api';
import { formatPrice } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { 
  BookOpen, Package, FileText, Clock, CheckCircle, ArrowRight, 
  CreditCard, Loader2, AlertTriangle, Star, Zap, Crown, Diamond,
  Video, MessageCircle, Filter, Sparkles, Building2, Copy, Upload, Image as ImageIcon, Banknote, QrCode
} from 'lucide-react';
import { toast } from 'sonner';

const PROGRAMS = [
  'UTBK-SNBT',
  'CPNS',
  'TNI',
  'POLRI',
  'SEKOLAH KEDINASAN',
  'BUMN',
  'TKA',
  'SMA Favorit'
];

const PACKAGE_ICONS = {
  'basic': <Package className="w-6 h-6" />,
  'silver': <Star className="w-6 h-6" />,
  'gold': <Crown className="w-6 h-6" />,
  'platinum': <Diamond className="w-6 h-6" />
};

const PACKAGE_COLORS = {
  'basic': 'border-slate-300 bg-slate-50',
  'silver': 'border-slate-400 bg-gradient-to-b from-slate-100 to-slate-200',
  'gold': 'border-amber-400 bg-gradient-to-b from-amber-50 to-amber-100',
  'platinum': 'border-purple-400 bg-gradient-to-b from-purple-50 to-purple-100'
};

export default function PackagesPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [tryouts, setTryouts] = useState([]);
  const [zoomClasses, setZoomClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Initialize from URL params
  const initialProgram = searchParams.get('program') || 'all';
  const [selectedProgram, setSelectedProgram] = useState(initialProgram);
  const [selectedType, setSelectedType] = useState('tryout');
  const [paymentDialog, setPaymentDialog] = useState({ open: false, package: null });
  const [mockPaymentDialog, setMockPaymentDialog] = useState({ open: false, orderId: null, packageName: '', amount: 0 });
  const [manualPaymentDialog, setManualPaymentDialog] = useState({ open: false, package: null });
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [snapReady, setSnapReady] = useState(false);
  const [isMockMode, setIsMockMode] = useState(false);
  const [bankInfo, setBankInfo] = useState(null);
  
  // Manual payment form state
  const [manualPaymentForm, setManualPaymentForm] = useState({
    bank_name: '',
    sender_name: '',
    transfer_amount: '',
    notes: '',
    payment_proof: null,
    payment_proof_url: ''
  });
  const [uploadingProof, setUploadingProof] = useState(false);

  // Sync URL params when program changes
  const handleProgramChange = (value) => {
    setSelectedProgram(value);
    if (value === 'all') {
      searchParams.delete('program');
    } else {
      searchParams.set('program', value);
    }
    setSearchParams(searchParams);
  };

  // Load Midtrans Snap.js script
  useEffect(() => {
    const loadMidtransScript = async () => {
      try {
        const keyResponse = await paymentAPI.getClientKey();
        const { client_key, is_production, mock_mode } = keyResponse.data;
        
        if (mock_mode) {
          console.log('Payment system running in MOCK MODE');
          setIsMockMode(true);
          setSnapReady(true);
          return;
        }
        
        setIsMockMode(false);
        
        const existingScript = document.getElementById('midtrans-snap');
        if (existingScript) {
          if (window.snap) {
            setSnapReady(true);
          } else {
            existingScript.onload = () => setSnapReady(true);
          }
          return;
        }
        
        const script = document.createElement('script');
        script.id = 'midtrans-snap';
        script.src = is_production 
          ? 'https://app.midtrans.com/snap/snap.js'
          : 'https://app.sandbox.midtrans.com/snap/snap.js';
        script.setAttribute('data-client-key', client_key);
        script.async = true;
        script.onload = () => {
          console.log('Midtrans Snap loaded successfully');
          setSnapReady(true);
        };
        script.onerror = (err) => {
          console.error('Failed to load Midtrans Snap:', err);
          toast.error('Gagal memuat sistem pembayaran');
        };
        document.head.appendChild(script);
      } catch (err) {
        console.error('Failed to initialize payment system:', err);
        setIsMockMode(true);
        setSnapReady(true);
      }
    };

    loadMidtransScript();
  }, []);

  useEffect(() => {
    loadData();
    loadBankInfo();
  }, [selectedProgram, selectedType]);

  const loadBankInfo = async () => {
    try {
      const settingsRes = await landingSettingsAPI.getPublic();
      const settings = settingsRes.data || {};
      if (settings.bank_name && settings.bank_account_number) {
        setBankInfo({
          bank_name: settings.bank_name,
          account_number: settings.bank_account_number,
          account_name: settings.bank_account_name || '',
          note: settings.bank_info_note || ''
        });
      }
    } catch (err) {
      console.log('Bank info not available');
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedProgram !== 'all') params.program = selectedProgram;
      if (selectedType !== 'all') params.package_type = selectedType;
      
      const [pkgRes, toRes, zoomRes] = await Promise.all([
        publicAPI.getPackages(params),
        publicAPI.getTryouts(selectedProgram !== 'all' ? selectedProgram : null),
        publicAPI.getZoomClasses(selectedProgram !== 'all' ? selectedProgram : null)
      ]);
      setPackages(pkgRes.data || []);
      setTryouts(toRes.data || []);
      setZoomClasses(zoomRes.data || []);
    } catch (err) {
      console.error('Failed to load packages:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTryoutsByIds = (ids) => {
    return tryouts.filter(t => ids.includes(t.id));
  };

  const handleBuyPackage = (pkg) => {
    if (!user) {
      toast.info('Silakan login terlebih dahulu');
      return;
    }
    
    if (pkg.final_price === 0) {
      toast.info('Paket ini gratis! Silakan akses dari dashboard.');
      return;
    }
    
    setPaymentDialog({ open: true, package: pkg });
  };

  const handleWhatsApp = () => {
    const phone = '6281262017001'; // WhatsApp admin MyTentor
    const message = encodeURIComponent('Halo, saya tertarik dengan eBook di Bimbel My Tentor');
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  const processPayment = async () => {
    if (!paymentDialog.package || !user) {
      toast.error('Data tidak lengkap');
      return;
    }

    if (!snapReady) {
      toast.error('Sistem pembayaran belum siap. Mohon tunggu atau refresh halaman.');
      return;
    }

    setPaymentLoading(true);

    try {
      const response = await paymentAPI.create({
        package_id: paymentDialog.package.id,
        user_email: user.email,
        user_name: user.name,
        user_phone: user.phone || ''
      });

      const { transaction_token, order_id, gross_amount } = response.data;

      if (!transaction_token) {
        throw new Error('Gagal mendapatkan token pembayaran');
      }

      setPaymentDialog({ open: false, package: null });
      setPaymentLoading(false);

      if (isMockMode || transaction_token.startsWith('MOCK-TOKEN')) {
        setMockPaymentDialog({
          open: true,
          orderId: order_id,
          packageName: paymentDialog.package.title,
          amount: gross_amount
        });
        return;
      }

      if (!window.snap) {
        toast.error('Midtrans belum dimuat. Silakan refresh halaman.');
        return;
      }

      window.snap.pay(transaction_token, {
        onSuccess: function(result) {
          console.log('Payment success:', result);
          toast.success('Pembayaran berhasil! Paket sudah aktif.');
          loadData();
        },
        onPending: function(result) {
          console.log('Payment pending:', result);
          toast.info('Pembayaran pending. Silakan selesaikan pembayaran.');
        },
        onError: function(result) {
          console.error('Payment error:', result);
          toast.error('Pembayaran gagal. Silakan coba lagi.');
        },
        onClose: function() {
          toast.info('Pembayaran dibatalkan');
        }
      });

    } catch (err) {
      console.error('Payment error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Gagal memproses pembayaran';
      toast.error(errorMessage);
      setPaymentLoading(false);
    }
  };

  const confirmMockPayment = async () => {
    if (!mockPaymentDialog.orderId) return;
    
    setPaymentLoading(true);
    try {
      await paymentAPI.confirmMock(mockPaymentDialog.orderId);
      toast.success('Pembayaran berhasil! Paket sudah aktif.');
      setMockPaymentDialog({ open: false, orderId: null, packageName: '', amount: 0 });
      loadData();
    } catch (err) {
      console.error('Mock payment confirmation error:', err);
      toast.error('Gagal mengkonfirmasi pembayaran');
    } finally {
      setPaymentLoading(false);
    }
  };

  // Handle manual payment dialog open
  const openManualPaymentDialog = (pkg) => {
    setManualPaymentDialog({ open: true, package: pkg });
    setManualPaymentForm({
      bank_name: '',
      sender_name: '',
      transfer_amount: pkg?.final_price?.toString() || '',
      notes: '',
      payment_proof: null,
      payment_proof_url: ''
    });
  };

  // Handle payment proof upload
  const handlePaymentProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('File harus berupa gambar (JPG, PNG, GIF, WebP)');
      return;
    }

    // Validate size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 5MB');
      return;
    }

    setUploadingProof(true);
    try {
      const response = await uploadAPI.uploadPaymentProof(file);
      setManualPaymentForm(prev => ({
        ...prev,
        payment_proof: file,
        payment_proof_url: response.data.url
      }));
      toast.success('Bukti pembayaran berhasil diunggah');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Gagal mengunggah bukti pembayaran');
    } finally {
      setUploadingProof(false);
    }
  };

  // Submit manual payment
  const submitManualPayment = async () => {
    if (!manualPaymentDialog.package || !user) {
      toast.error('Data tidak lengkap');
      return;
    }

    if (!manualPaymentForm.payment_proof_url) {
      toast.error('Silakan upload bukti pembayaran');
      return;
    }

    if (!manualPaymentForm.bank_name.trim()) {
      toast.error('Silakan isi nama bank');
      return;
    }

    if (!manualPaymentForm.sender_name.trim()) {
      toast.error('Silakan isi nama pengirim');
      return;
    }

    setPaymentLoading(true);
    try {
      await purchaseAPI.request({
        item_type: 'package',
        item_id: manualPaymentDialog.package.id,
        payment_proof: manualPaymentForm.payment_proof_url,
        payment_method: 'manual_transfer',
        bank_name: manualPaymentForm.bank_name,
        sender_name: manualPaymentForm.sender_name,
        transfer_amount: parseInt(manualPaymentForm.transfer_amount) || 0,
        notes: manualPaymentForm.notes
      });

      toast.success('Permintaan pembelian berhasil dikirim! Menunggu konfirmasi admin.');
      setManualPaymentDialog({ open: false, package: null });
      setManualPaymentForm({
        bank_name: '',
        sender_name: '',
        transfer_amount: '',
        notes: '',
        payment_proof: null,
        payment_proof_url: ''
      });
    } catch (err) {
      console.error('Manual payment error:', err);
      const errorMessage = err.response?.data?.detail || 'Gagal mengirim permintaan pembelian';
      toast.error(errorMessage);
    } finally {
      setPaymentLoading(false);
    }
  };

  const getPackageTier = (pkg) => {
    const count = pkg.tryout_count || pkg.zoom_class_ids?.length || 0;
    if (count >= 20) return 'platinum';
    if (count >= 15) return 'gold';
    if (count >= 10) return 'silver';
    return 'basic';
  };

  const renderPackageCard = (pkg) => {
    const tier = getPackageTier(pkg);
    const includedTryouts = getTryoutsByIds(pkg.tryout_ids || []);
    
    return (
      <Card 
        key={pkg.id} 
        className={`relative overflow-hidden hover:shadow-lg transition-all duration-300 ${PACKAGE_COLORS[tier]}`}
        data-testid={`package-card-${pkg.id}`}
      >
        {/* Discount Badge */}
        {pkg.discount_percent > 0 && (
          <div className="absolute top-2 right-2">
            <Badge className="bg-red-500 text-white hover:bg-red-500 text-xs px-1.5 py-0.5">
              -{pkg.discount_percent}%
            </Badge>
          </div>
        )}

        <CardHeader className="p-3 pb-1">
          <div className="flex items-center gap-2 mb-1">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              tier === 'platinum' ? 'bg-purple-500 text-white' :
              tier === 'gold' ? 'bg-amber-500 text-white' :
              tier === 'silver' ? 'bg-slate-400 text-white' :
              'bg-slate-200 text-slate-600'
            }`}>
              {pkg.package_type === 'zoom' ? <Video className="w-4 h-4" /> : PACKAGE_ICONS[tier]}
            </div>
            <div className="flex-1 min-w-0">
              <Badge variant="outline" className="text-[10px] px-1 py-0 mb-0.5">{pkg.program || 'Umum'}</Badge>
              <CardTitle className="text-sm font-semibold truncate">{pkg.title}</CardTitle>
            </div>
          </div>
          <CardDescription className="text-xs line-clamp-2">{pkg.description}</CardDescription>
        </CardHeader>

        <CardContent className="p-3 pt-1 space-y-2">
          {/* Price */}
          <div className="flex items-end gap-1">
            {pkg.discount_percent > 0 && (
              <span className="text-slate-400 line-through text-xs">
                {formatPrice(pkg.price)}
              </span>
            )}
            <span className={`text-lg font-bold ${
              tier === 'platinum' ? 'text-purple-600' :
              tier === 'gold' ? 'text-amber-600' :
              tier === 'silver' ? 'text-slate-600' :
              'text-[#4472C4]'
            }`}>
              {pkg.final_price === 0 ? 'GRATIS' : formatPrice(pkg.final_price)}
            </span>
          </div>

          {/* Features - Compact */}
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-slate-600">
              {pkg.package_type === 'zoom' ? (
                <>
                  <Video className="w-3 h-3 text-blue-500" />
                  <span>{pkg.zoom_class_ids?.length || 0}x Kelas Zoom</span>
                </>
              ) : (
                <>
                  <FileText className="w-3 h-3 text-[#4472C4]" />
                  <span>{pkg.tryout_count}x Try Out</span>
                </>
              )}
            </div>
            
            {pkg.features?.length > 0 && (
              <ul className="space-y-0.5">
                {pkg.features.slice(0, 2).map((feature, i) => (
                  <li key={i} className="flex items-center gap-1.5 text-xs text-slate-600">
                    <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                    <span className="truncate">{feature}</span>
                  </li>
                ))}
                {pkg.features.length > 2 && (
                  <li className="text-[10px] text-slate-400 pl-4">+{pkg.features.length - 2} lainnya</li>
                )}
              </ul>
            )}
          </div>

          {/* Included Items - More compact */}
          {pkg.package_type === 'tryout' && includedTryouts.length > 0 && (
            <div className="pt-2 border-t border-slate-200">
              <p className="text-[10px] font-medium text-slate-500 mb-1">Termasuk:</p>
              <div className="space-y-0.5">
                {includedTryouts.slice(0, 2).map((to) => (
                  <div key={to.id} className="flex items-center gap-1.5 text-[10px] text-slate-600">
                    <CheckCircle className="w-2.5 h-2.5 text-green-500" />
                    <span className="truncate">{to.title}</span>
                  </div>
                ))}
                {includedTryouts.length > 2 && (
                  <p className="text-[10px] text-slate-400 pl-3">+{includedTryouts.length - 2} lainnya</p>
                )}
              </div>
            </div>
          )}
        </CardContent>

        <CardFooter className="p-3 pt-0">
          <Button 
            size="sm"
            className={`w-full text-xs h-8 ${
              tier === 'platinum' ? 'bg-purple-600 hover:bg-purple-700' :
              tier === 'gold' ? 'bg-amber-500 hover:bg-amber-600' :
              tier === 'silver' ? 'bg-slate-500 hover:bg-slate-600' :
              'bg-[#FF6B35] hover:bg-[#e55a2a]'
            }`}
            onClick={() => handleBuyPackage(pkg)}
            data-testid={`buy-package-${pkg.id}`}
          >
            <CreditCard className="w-3 h-3 mr-1" />
            {pkg.final_price === 0 ? 'Ambil Gratis' : 'Beli Sekarang'}
          </Button>
        </CardFooter>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="packages-page">
      {/* Header with Mobile Navigation */}
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-r from-[#4472C4] to-[#5a8dd6] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Badge className="bg-white/20 text-white hover:bg-white/30 border-0 mb-4">
            <Sparkles className="w-3 h-3 mr-1" />
            Paket Hemat
          </Badge>
          <h1 className="text-4xl font-bold mb-4">Pilih Paket Belajarmu</h1>
          <p className="text-lg opacity-90 max-w-2xl mx-auto">
            Paket Try Out, Kelas Zoom, dan eBook dengan harga terjangkau
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 px-4 bg-white border-b sticky top-[57px] z-40">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <span className="text-sm font-medium text-slate-600">Program:</span>
              </div>
              <Select value={selectedProgram} onValueChange={setSelectedProgram}>
                <SelectTrigger className="w-[200px]" data-testid="program-filter">
                  <SelectValue placeholder="Semua Program" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Program</SelectItem>
                  {PROGRAMS.map(prog => (
                    <SelectItem key={prog} value={prog}>{prog}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Tabs value={selectedType} onValueChange={setSelectedType} className="w-full sm:w-auto">
              <TabsList className="grid grid-cols-3 w-full sm:w-auto">
                <TabsTrigger value="tryout" className="flex items-center gap-1" data-testid="tab-tryout">
                  <FileText className="w-4 h-4" />
                  <span className="hidden sm:inline">Try Out</span>
                </TabsTrigger>
                <TabsTrigger value="zoom" className="flex items-center gap-1" data-testid="tab-zoom">
                  <Video className="w-4 h-4" />
                  <span className="hidden sm:inline">Zoom</span>
                </TabsTrigger>
                <TabsTrigger value="ebook" className="flex items-center gap-1" data-testid="tab-ebook">
                  <BookOpen className="w-4 h-4" />
                  <span className="hidden sm:inline">eBook</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-[#4472C4] border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="mt-4 text-slate-500">Memuat paket...</p>
            </div>
          ) : selectedType === 'ebook' ? (
            /* eBook Section - WhatsApp CTA */
            <div className="text-center py-12">
              <div className="max-w-md mx-auto">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <BookOpen className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">eBook Premium</h3>
                <p className="text-slate-500 mb-6">
                  Dapatkan eBook materi lengkap untuk persiapan ujian. Hubungi admin via WhatsApp untuk pembelian.
                </p>
                <Button 
                  className="bg-green-500 hover:bg-green-600 text-white px-8"
                  onClick={handleWhatsApp}
                  data-testid="whatsapp-ebook-btn"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Hubungi via WhatsApp
                </Button>
              </div>
            </div>
          ) : packages.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Belum Ada Paket</h3>
              <p className="text-slate-500 mb-6">
                {selectedType === 'zoom' 
                  ? 'Paket Zoom akan segera tersedia' 
                  : 'Paket Try Out akan segera tersedia'}
              </p>
              <Link to="/tryouts">
                <Button className="bg-[#4472C4] hover:bg-[#3861a8]">
                  Lihat Try Out Satuan
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {packages.map(renderPackageCard)}
            </div>
          )}
        </div>
      </section>

      {/* Individual Tryouts Section */}
      {selectedType === 'tryout' && tryouts.length > 0 && (
        <section className="py-12 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900">Atau Beli Satuan</h2>
              <p className="text-slate-500 mt-2">Pilih try out individual sesuai kebutuhan</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tryouts.slice(0, 6).map((to) => (
                <div key={to.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-[#4472C4] transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#4472C4]/10 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-[#4472C4]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 text-sm">{to.title}</h4>
                      <p className="text-xs text-slate-500">{to.question_count} soal - {to.category}</p>
                    </div>
                  </div>
                  <Badge className={to.price === 0 ? 'bg-green-100 text-green-700' : 'bg-[#4472C4] text-white'}>
                    {to.price === 0 ? 'GRATIS' : formatPrice(to.price)}
                  </Badge>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <Link to="/tryouts">
                <Button variant="outline">
                  Lihat Semua Try Out
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Payment Dialog - Only Manual Transfer (No Midtrans) */}
      <Dialog open={paymentDialog.open} onOpenChange={(open) => !open && setPaymentDialog({ open: false, package: null })}>
        <DialogContent className="max-w-xs">
          <DialogHeader className="pb-1">
            <DialogTitle className="text-sm flex items-center gap-2">
              <Banknote className="w-4 h-4 text-amber-600" />
              Bayar Paket
            </DialogTitle>
          </DialogHeader>
          
          {paymentDialog.package && (
            <div className="space-y-2 text-xs">
              <div className="bg-slate-50 p-2 rounded-lg">
                <h3 className="font-semibold text-slate-900 text-sm truncate">{paymentDialog.package.title}</h3>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-slate-500">Total</span>
                  <span className="text-base font-bold text-[#4472C4]">
                    {formatPrice(paymentDialog.package.final_price)}
                  </span>
                </div>
              </div>

              <p className="text-slate-600">
                Lanjutkan ke form transfer untuk upload bukti pembayaran
              </p>
            </div>
          )}

          <DialogFooter className="gap-1 pt-1">
            <Button variant="outline" size="sm" className="h-7 text-xs"
              onClick={() => setPaymentDialog({ open: false, package: null })}
            >
              Batal
            </Button>
            <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700"
              onClick={() => {
                setPaymentDialog({ open: false, package: null });
                openManualPaymentDialog(paymentDialog.package);
              }}
              data-testid="manual-payment-btn"
            >
              <Banknote className="w-3 h-3 mr-1" />
              Transfer Sekarang
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Payment Dialog - Compact & Scrollable for Mobile */}
      <Dialog open={manualPaymentDialog.open} onOpenChange={(open) => !open && setManualPaymentDialog({ open: false, package: null })}>
        <DialogContent className="max-w-sm max-h-[90vh] flex flex-col">
          <DialogHeader className="pb-1 flex-shrink-0">
            <DialogTitle className="flex items-center gap-2 text-sm">
              <Banknote className="w-4 h-4 text-amber-600" />
              Transfer Bank
            </DialogTitle>
          </DialogHeader>
          
          {manualPaymentDialog.package && (
            <div className="space-y-2 text-xs overflow-y-auto flex-1 pr-1">
              {/* Package Info - Compact */}
              <div className="bg-slate-50 p-2 rounded-lg">
                <h3 className="font-semibold text-slate-900 text-sm truncate">{manualPaymentDialog.package.title}</h3>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-slate-500">Total</span>
                  <span className="text-base font-bold text-[#4472C4]">
                    {formatPrice(manualPaymentDialog.package.final_price)}
                  </span>
                </div>
              </div>

              {/* Bank Transfer & QRIS - Stacked on small mobile, side by side on larger screens */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {/* Bank Transfer Info - More Compact with overflow handling */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-1.5">
                  <div className="flex items-center gap-1 text-blue-800 font-medium mb-1 text-[10px]">
                    <Building2 className="w-3 h-3 shrink-0" />
                    <span>Transfer ke:</span>
                  </div>
                  <div className="space-y-0.5 text-[9px]">
                    {[
                      { bank: 'BCA', no: '1950947191' },
                      { bank: 'BNI', no: '0340100950' },
                      { bank: 'Mandiri', no: '106-00-1301771-3' },
                      { bank: 'BRI', no: '0336-01-092728-50-2' },
                      { bank: 'E-Wallet', no: '081262017001' },
                    ].map((acc) => (
                      <div key={acc.bank} className="flex items-center justify-between bg-white px-1 py-0.5 rounded overflow-hidden">
                        <span className="text-slate-600 font-medium shrink-0">{acc.bank}</span>
                        <div className="flex items-center gap-0.5 min-w-0">
                          <span className="font-mono text-blue-700 text-[8px] truncate">{acc.no}</span>
                          <button 
                            onClick={() => {
                              navigator.clipboard.writeText(acc.no);
                              toast.success(`${acc.bank} disalin!`);
                            }}
                            className="p-0.5 hover:bg-blue-100 rounded shrink-0"
                          >
                            <Copy className="w-2.5 h-2.5 text-blue-700" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-center text-[8px] text-blue-800 font-medium mt-1 pt-1 border-t border-blue-200">
                    a.n. Togi Parsaoran S. Pasaribu, S.Pd
                  </p>
                </div>

                {/* QRIS Payment - Smaller */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-1.5">
                  <div className="flex items-center gap-1 text-emerald-800 font-medium mb-1 text-[10px]">
                    <QrCode className="w-3 h-3 shrink-0" />
                    <span>QRIS</span>
                  </div>
                  <div className="flex justify-center">
                    <img 
                      src="/images/qris-mytentor.png" 
                      alt="QRIS MyTentor" 
                      className="w-20 h-20 sm:w-24 sm:h-24 object-contain"
                    />
                  </div>
                  <p className="text-center text-[8px] text-emerald-700 mt-0.5">
                    GoPay, OVO, DANA, dll
                  </p>
                </div>
              </div>

              {/* Payment Form - Compact */}
              <div className="space-y-1.5">
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-0.5">
                    <Label className="text-[10px]">Bank Pengirim *</Label>
                    <Input
                      placeholder="BCA, Mandiri..."
                      value={manualPaymentForm.bank_name}
                      onChange={(e) => setManualPaymentForm(prev => ({ ...prev, bank_name: e.target.value }))}
                      className="h-7 text-xs"
                      data-testid="input-bank-name"
                    />
                  </div>
                  <div className="space-y-0.5">
                    <Label className="text-[10px]">Nama Pengirim *</Label>
                    <Input
                      placeholder="Nama rekening"
                      value={manualPaymentForm.sender_name}
                      onChange={(e) => setManualPaymentForm(prev => ({ ...prev, sender_name: e.target.value }))}
                      className="h-7 text-xs"
                      data-testid="input-sender-name"
                    />
                  </div>
                </div>

                {/* Upload Payment Proof - More Compact */}
                <div className="space-y-0.5">
                  <Label className="text-[10px]">Bukti Transfer *</Label>
                  <div className="border border-dashed border-slate-300 rounded p-1.5 text-center hover:border-amber-400 transition-colors">
                    {manualPaymentForm.payment_proof_url ? (
                      <div className="flex items-center justify-center gap-2">
                        <img 
                          src={manualPaymentForm.payment_proof_url} 
                          alt="Bukti" 
                          className="h-12 rounded border"
                        />
                        <div className="text-left">
                          <p className="text-[10px] text-green-600 flex items-center gap-0.5">
                            <CheckCircle className="w-3 h-3" />
                            Berhasil
                          </p>
                          <button
                            onClick={() => setManualPaymentForm(prev => ({ ...prev, payment_proof: null, payment_proof_url: '' }))}
                            className="text-[9px] text-red-500 hover:underline"
                          >
                            Hapus
                          </button>
                        </div>
                      </div>
                    ) : (
                      <label className="cursor-pointer flex items-center justify-center gap-2 py-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePaymentProofUpload}
                          className="hidden"
                          disabled={uploadingProof}
                          data-testid="input-payment-proof"
                        />
                        {uploadingProof ? (
                          <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />
                        ) : (
                          <>
                            <Upload className="w-4 h-4 text-amber-600" />
                            <span className="text-[10px] text-slate-600">Upload bukti</span>
                          </>
                        )}
                      </label>
                    )}
                  </div>
                </div>
              </div>

              <p className="text-[9px] text-amber-700 bg-amber-50 p-1 rounded flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                Admin verifikasi maks 1x24 jam
              </p>
            </div>
          )}

          <DialogFooter className="gap-1 pt-2 flex-shrink-0 border-t mt-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setManualPaymentDialog({ open: false, package: null })}
              className="h-8 text-xs"
            >
              Batal
            </Button>
            <Button 
              size="sm"
              onClick={submitManualPayment}
              disabled={paymentLoading || !manualPaymentForm.payment_proof_url}
              className="bg-amber-600 hover:bg-amber-700 h-8 text-xs"
              data-testid="submit-manual-payment-btn"
            >
              {paymentLoading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <>
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Kirim
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Footer />
    </div>
  );
}
